import { Island } from '../island.class';
import { ItemizedIslandMsrp } from '../types';
import { ItemizedLineItem } from '../../shared.types';

// Read through a cast until the catalog DataShapes type `discontinued`; an untyped catalog reads as not discontinued
const isDiscontinued = (record: unknown): boolean => (record as { discontinued?: boolean } | null)?.discontinued === true;

/**
 * Builds itemized MSRP for an island with items ordered by natural spatial position.
 * Traverses panels in their existing order, groups left-to-right within each panel,
 * and items top-to-bottom within each group. Duplicate SKUs are merged into the
 * first spatial occurrence.
 * @param island Island to calculate the itemized MSRP for
 * @returns ItemizedIslandMsrp
 */
export const getItemizedMsrpCfn = (island: Island): ItemizedIslandMsrp => {
	const countertop = island.getCountertop();
	const msrpItems: Array<ItemizedLineItem> = [];
	const { children: panels = [] } = island.panels;

	// Traverse panels in natural order, groups left-to-right, items top-to-bottom
	panels.forEach((panel) => {
		const sortedGroups = [...panel.panelItemGroups.children].sort((a, b) => a.center.x - b.center.x);

		sortedGroups.forEach((panelItemGroup) => {
			const sortedItems = [...panelItemGroup.items.children].sort((a, b) => a.center.y - b.center.y);

			sortedItems.forEach((panelItem) => {
				const { category, fqSku, fuel, showFuelSku, override } = panelItem.item;
				if (fqSku === 'CUSTOM') return;
				if (category?.isUtilityCutout) return;
				// Only skip categories that are DELIBERATELY inactive (e.g. system / utility cutouts).
				// A null/unresolved category (categoryId not present in the loaded catalog — as can happen
				// when a category-split migration reassigns SKUs to new category ids that the running
				// catalog has not caught up to) must NOT silently drop a placed, priced appliance from
				// every estimate surface. Price it anyway and surface the data gap instead of vanishing it.
				if (category && category.active === false) return;
				if (!category) {
					console.error(`getItemizedMsrp: item ${fqSku} has an unresolved category (categoryId not in catalog) — pricing it anyway`);
				}

				// Every hand-placed override shares one generic sku, so merging on sku alone would fold two differently-labelled cutouts into one row and lose a label (GitHub #513)
				const existing = msrpItems.find((item) => item.sku === fqSku && (!override || item.description === panelItem.item.description));
				const discontinued = isDiscontinued(panelItem.item.component);

				if (existing) {
					existing.qty += 1;
					existing.total += panelItem.item.msrp;
					if (discontinued) existing.discontinued = true;
					if (override) existing.isCustomOverride = true;
				} else {
					msrpItems.push({
						qty: 1,
						sku: fqSku,
						hsProductId: panelItem.item.hsProductId ?? null,
						isFuelVariant: fuel && showFuelSku,
						label: panelItem.item.label,
						category: category?.description ?? 'Uncategorized',
						description: panelItem.item.description,
						each: panelItem.item.msrp,
						total: panelItem.item.msrp,
						discontinued,
						isCustomOverride: override
					});
				}
			});
		});
	});

	const countertopItem = {
		qty: 1,
		sku: countertop?.sku ?? 'NO-SKU',
		category: 'Countertop',
		description: countertop?.material.displayName,
		each: countertop?.msrp,
		total: countertop?.msrp
	};

	return {
		countertop: countertopItem,
		island: island.msrpBase,
		description: island.description,
		items: msrpItems
	};
};
